
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { createPlan, updatePlan, getBranches } from "@/integrations/supabase/services";
import { useToast } from "@/hooks/use-toast";
import { Plan } from "@/types/models";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const formSchema = z.object({
  name: z.string().min(2, {
    message: "El nombre debe tener al menos 2 caracteres.",
  }),
  price: z.number().min(0.01, {
    message: "El precio debe ser mayor a 0.",
  }),
  time_duration: z.string().min(1, {
    message: "La duración es requerida.",
  }),
  extend: z.boolean(),
  active: z.boolean(),
  branch_id: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface PlanFormProps {
  plan?: Plan | null;
  onClose: () => void;
}

const PlanForm = ({ plan, onClose }: PlanFormProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: getBranches,
    enabled: profile?.role === 'super_admin',
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: plan?.name || "",
      price: plan?.price || 0,
      time_duration: plan?.time_duration || "",
      extend: plan?.extend || false,
      active: plan?.active ?? true,
      branch_id: plan?.branch_id || (profile?.branch_id ? profile.branch_id : ""),
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: Omit<Plan, 'id' | 'created_at' | 'updated_at' | 'deleted_at'>) => createPlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      toast({
        title: "Plan creado",
        description: "El plan ha sido creado correctamente.",
      });
      onClose();
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Error al crear el plan.",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Plan> }) =>
      updatePlan(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      toast({
        title: "Plan actualizado",
        description: "El plan ha sido actualizado correctamente.",
      });
      onClose();
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Error al actualizar el plan.",
      });
    },
  });

  const onSubmit = (values: FormValues) => {
    const branchId = values.branch_id || profile?.branch_id;

    if (!branchId && profile?.role === 'super_admin') {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Debe seleccionar una sucursal.",
      });
      return;
    }

    if (plan) {
      updateMutation.mutate({ id: plan.id, data: { ...values, branch_id: branchId } });
    } else {
      // Ensure all required fields are present for creation
      const planData: Omit<Plan, 'id' | 'created_at' | 'updated_at' | 'deleted_at'> = {
        name: values.name,
        price: values.price,
        time_duration: values.time_duration,
        active: values.active,
        branch_id: branchId,
        extend: values.extend,
      };
      createMutation.mutate(planData);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nombre</FormLabel>
              <FormControl>
                <Input placeholder="Nombre del plan" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="price"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Precio</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  {...field}
                  onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="time_duration"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Duración</FormLabel>
              <FormControl>
                <Input placeholder="ej: 30 min, 1 hora, 2 hora, etc." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {profile?.role === 'super_admin' && (
          <FormField
            control={form.control}
            name="branch_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Sucursal</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccione una sucursal" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {branches.map((branch) => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="extend"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Extensible</FormLabel>
                <div className="text-sm text-muted-foreground">
                  El plan será extensible en tiempo. <br/> <b>Los planes de 30 min no son extensibles</b>
                </div>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="active"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Estado</FormLabel>
                <div className="text-sm text-muted-foreground">
                  El plan estará disponible para alquileres
                </div>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />
        
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={isLoading}>
            {plan ? "Actualizar" : "Crear"}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default PlanForm;
